import type { O } from 'ts-toolbelt';
import type { Split } from 'ts-toolbelt/out/String/Split';

import type { ObjectPaths } from '@lokalise/ui/ts-utils';

import get from '../../utils/object/get';
import type { Theme } from '../types';
import fromTheme from './fromTheme';
import type { FromTheme } from './fromTheme';

/**
 * Reason we use interface this way is because we basically need to define
 * two types of signatures – one with fallback and one without.
 * When fallback provided, the prop is optional.
 * When fallback is not provided, the prop is required.
 * This means that one value will always be selected - either explicitly or by default.
 */
interface ValueFromProp {
    <
        Path extends ObjectPaths<Theme>,
        Prop extends string,
        Keys extends keyof O.Path<Theme, Split<Path, '.'>>,
        // We need this alias as otherwise type gets narrowed from 'a' | 'b' to 'a' when fallback is passed
        Fallback = Keys,
    >(
        prop: Prop,
        themePath: Path,
        fallback: Fallback,
    ): ReturnType<FromTheme<Partial<Record<Prop, Keys>>>>;
    <Path extends ObjectPaths<Theme>, Prop extends string, Keys extends keyof O.Path<Theme, Split<Path, '.'>>>(
        prop: Prop,
        themePath: Path,
    ): ReturnType<FromTheme<Record<Prop, Keys>>>;
}

const valueFromProp: ValueFromProp = (prop: string, path: unknown, fallback?: never) =>
    fromTheme((theme, props) => get(`${path}.${props[prop as keyof typeof props] ?? fallback}`, theme));

export default valueFromProp;
